import type { UrlStatusUpdate } from '@kanbu/schema/contracts';
import { DocumentStatus } from '@kanbu/schema/enums';
import { CircleCheck, Info } from 'lucide-preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/compat';

import { UrlList } from '@/components/url-list/url-list';

import { RegisterForm } from './components/register-form/register-form';
import { StatusBar } from './components/status-bar/status-bar';
import { Wrapper } from './components/wrapper/wrapper';
import { AppSettings } from './constants/app-settings';

export function App() {
  // Get references to DOM elements
  const urlInputElement = document.getElementById(
    'url-input',
  ) as HTMLInputElement;
  const submitButtonElement = document.getElementById(
    'submit-button',
  ) as HTMLAnchorElement;
  const submitButtonMobileElement = document.getElementById(
    'submit-button-mobile',
  ) as HTMLInputElement;

  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [chatId, setChatId] = useState('');
  const [urlStatusUpdates, setUrlStatusUpdates] = useState<UrlStatusUpdate[]>([
    // {
    //   id: '1',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.Done,
    // },
    // {
    //   id: '2',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.Done,
    // },
    // {
    //   id: '3',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.Done,
    // },
    // {
    //   id: '4',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.Done,
    // },
    // {
    //   id: '5',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.Embedding,
    // },
    // {
    //   id: '6',
    //   url: 'https://www.utima.com/lorem-ipsum/dolor-sit-amet/lorem-ipsum/dolor-sit-amet/lorem-ipsum/dolor-sit-amet',
    //   status: DocumentStatus.Embedding,
    // },
    // {
    //   id: '7',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.EmbeddingError,
    // },
    // {
    //   id: '8',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.EmbeddingError,
    // },
    // {
    //   id: '9',
    //   url: 'https://www.utima.com',
    //   status: DocumentStatus.Embedding,
    // },
  ]);

  const isUnused = !isLoading && urlStatusUpdates.length === 0;
  const isDone = !isLoading && organizationId && chatId;

  const onInput = useCallback(
    (event: Event) => {
      setUrl((event.target as HTMLInputElement).value);
    },
    [setUrl],
  );

  const onSubmit = useCallback(
    async (event: Event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (isLoading || !url) {
        urlInputElement.form?.reportValidity();

        return;
      }

      setUrlStatusUpdates([]);
      setOrganizationId('');

      const enableSubmitButtonElement = () => {
        // Disable button during processing
        submitButtonElement.style.pointerEvents = 'auto';
        submitButtonElement.style.opacity = '1';
        submitButtonMobileElement.disabled = false;
        submitButtonMobileElement.style.opacity = '1';
        setIsLoading(false);
      };

      submitButtonElement.style.pointerEvents = 'none';
      submitButtonElement.style.opacity = '0.5';
      submitButtonMobileElement.disabled = true;
      submitButtonMobileElement.style.opacity = '0.5';
      setIsLoading(true);

      const response = await fetch(`${AppSettings.apiUrl}/agents/easy-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: new URL(url.startsWith('http') ? url : `https://${url}`).href,
        }),
      });

      if (!response.ok || !response.body) {
        // TODO: Handle error and show error message to user
        console.error('Failed to fetch');
        enableSubmitButtonElement();

        return;
      }

      // Process the response body stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        try {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          const chunks = decoder
            .decode(value, { stream: true })
            .split('\n')
            .filter(line => line.trim() !== '');

          for (const chunk of chunks) {
            let message:
              | UrlStatusUpdate[]
              | { chatId: string; organizationId: string };

            // Decode and log the chunk
            try {
              message = JSON.parse(chunk);
            } catch (error) {
              console.error('Error parsing JSON:', error, chunk);

              continue;
            }

            if (Array.isArray(message)) {
              setUrlStatusUpdates(prev => [
                ...prev,
                ...message.filter(
                  urlStatusUpdate =>
                    prev.find(
                      prevUrlStatusUpdate =>
                        prevUrlStatusUpdate.id === urlStatusUpdate.id,
                    )?.status !== DocumentStatus.Done,
                ),
              ]);
            } else {
              setChatId(message.chatId);
              setOrganizationId(message.organizationId);
            }
          }
        } catch (error) {
          // TODO: Handle error and show error message to user
          console.error('Error reading stream:', error);
          break;
        }
      }

      enableSubmitButtonElement();
    },
    [
      isLoading,
      url,
      submitButtonElement,
      submitButtonMobileElement,
      urlInputElement,
    ],
  );

  const onOpenChat = useCallback(() => {
    for (const chatbotElement of document.getElementsByTagName(
      'kanbu-chatbot',
    )) {
      chatbotElement.remove();
    }

    const chatbotElement = document.createElement('kanbu-chatbot');
    chatbotElement.setAttribute('chat', chatId);
    chatbotElement.setAttribute('opened', '');
    document.body.append(chatbotElement);
  }, [chatId]);

  useEffect(() => {
    if (
      !urlInputElement ||
      !submitButtonElement ||
      !submitButtonMobileElement
    ) {
      console.error('Required DOM elements not found.');

      return;
    }

    urlInputElement.addEventListener('input', onInput);
    submitButtonElement.addEventListener('click', onSubmit);
    submitButtonMobileElement.addEventListener('click', onSubmit);

    return () => {
      urlInputElement.removeEventListener('input', onInput);
      submitButtonElement.removeEventListener('click', onSubmit);
      submitButtonMobileElement.removeEventListener('click', onSubmit);
    };
  }, [
    onInput,
    onSubmit,
    submitButtonElement,
    submitButtonMobileElement,
    urlInputElement,
  ]);

  const filteredUrlStatusUpdates = useMemo(() => {
    const newUrlStatusUpdates = new Map<string, UrlStatusUpdate>();

    for (const urlStatusUpdate of urlStatusUpdates) {
      newUrlStatusUpdates.set(urlStatusUpdate.id, urlStatusUpdate);
    }

    return Array.from(newUrlStatusUpdates.values());
  }, [urlStatusUpdates]);

  return (
    <div className='kanbu-web-input'>
      <div className='flex flex-col gap-4'>
        {isDone && (
          <Wrapper className='!border-[#22c55e80]'>
            <div className='flex items-center justify-between gap-2'>
              <div className='flex items-center gap-1'>
                <CircleCheck
                  className='size-6 shrink-0 text-[#22c55e]'
                  strokeWidth={1}
                />
                <span className='text-[14px] leading-none text-[#16a34a]'>
                  Váš chatbot byl úspěšně natrénován!
                </span>
              </div>
              <button
                data-cta='web-input-open-chat-button'
                className='rounded-[16px] border border-[#9c70ff] px-4 py-3 text-[14px] font-medium text-[#9c70ff]'
                onClick={onOpenChat}
                type='button'
              >
                Otevřít chat
              </button>
            </div>
          </Wrapper>
        )}
        {(isLoading || isUnused) && (
          <Wrapper>
            {isUnused && (
              <div className='flex items-center justify-center gap-2'>
                <Info className='size-4 shrink-0 text-[#1a1a1ab2]' />
                <span className='text-[11px] font-medium leading-[18px] text-[#1a1a1ab2]'>
                  Zadáním Vaší adresy započne proces učení. Načtu si informace z
                  Vašich veřejných stránek pro zpracování obsahu a chytrou
                  analýzu k vytvoření AI agenta.
                </span>
              </div>
            )}
            {isLoading && <StatusBar />}
            <UrlList urls={filteredUrlStatusUpdates} isLoading={isLoading} />
          </Wrapper>
        )}
        {isDone && (
          <Wrapper title='Zdroje, které znám'>
            <UrlList urls={filteredUrlStatusUpdates} isLoading={false} />
          </Wrapper>
        )}
        {isDone && (
          <Wrapper title='Jak to funguje?'>
            <p className='text-[14px] leading-[24px] text-[#1a1a1a]'>
              Otevřete{' '}
              <b className='font-medium'>
                chatovací okno v pravé dolní části obrazovky.
              </b>{' '}
              Zeptejte se mě z pozice Vašeho zákazníka. Zvládnu odpovědi jako
              <b className='font-medium'> reálný kolega.</b> Pro nahrání dalších
              datových zdrojů, úpravy chování či vzhledu AI chatbotu{' '}
              <b className='font-medium'>zadejte e-mail</b>, na který Vám z
              bezpečnostních důvodů přijde potvrzovací odkaz. Dále pokračujte
              dle našich návodů.
            </p>
            <RegisterForm organizationId={organizationId} />
          </Wrapper>
        )}
      </div>
    </div>
  );
}
